// Flowgate integration endpoints. Flowgate is the source of truth for pipeline
// definitions; Django stores only the experiment's flowgate_pipeline_id and the
// result metadata Flowgate pushes back.

export type FlowgatePipeline = {
  id: string;
  name: string;
  description: string;
};

export type FlowgatePipelineList = FlowgatePipeline[];

// GET /flowgate/pipelines/<id>/ is a passthrough of Flowgate's metadata +
// input-schema; the exact shape is owned by Flowgate, so keep it open.
export type FlowgatePipelineDetail = {
  id: string;
  name: string;
  description?: string;
  [key: string]: unknown;
};

export type RunJobRequest = {
  experiment_id: string;
  params?: Record<string, unknown>;
};

// POST /flowgate/run_job/ submits inline and returns the real outcome. A 200
// means the run started: status is 'in_progress' and the body carries the
// pipeline-service batch job id. Completion is still async (poll the experiment
// status endpoint to see it move to completed/failed).
export type RunJobResponse = {
  id: string;
  batch_job_id: string;
  status: 'in_progress';
};

// Error bodies returned by run_job:
//   422 — pipeline service rejected the inputs (actionable validation errors)
//   400 — experiment misconfigured (e.g. no pipeline selected)
//   503 — pipeline service unreachable (nothing was submitted)
export type RunJobErrorResponse = {
  detail?: string;
  errors?: string[];
};

export type FlowgateResult = {
  id: string;
  experiment: string;
  flowgate_job_id: string;
  name: string;
  file_url: string;
  file_type: string;
  row_count: number | null;
  columns: unknown | null;
  metadata: unknown | null;
  created_at: string;
  // Phase-2 stable per-row id flag. When false, the result predates `__row_id`
  // and selection/validation endpoints will refuse mutations with 409 no_row_id.
  // Optional because pre-phase-2 BE responses won't carry it.
  has_row_id?: boolean;
};

export type FlowgateResultList = FlowgateResult[];

// ──────────────────────────────────────────────────────────────────────────────
// Result data API (per-result schema / query / distinct / download)
// ──────────────────────────────────────────────────────────────────────────────

// DuckDB type string from the BE — e.g. VARCHAR, BIGINT, INTEGER, DOUBLE,
// FLOAT, BOOLEAN, DATE, TIMESTAMP, DECIMAL(p,s). Left as `string` so
// unexpected types still parse; callers narrow via the column-kind helper.
export type FlowgateColumnType = string;

export type FlowgateColumn = {
  name: string;
  type: FlowgateColumnType;
};

// GET /flowgate/results/<id>/schema/
export type FlowgateResultSchemaResponse = {
  name: string;
  columns: FlowgateColumn[];
  row_count: number | null;
  // True when the BE result includes the stable __row_id column (phase 2).
  // Optional for forward-compat with older BE deploys that don't emit it.
  has_row_id?: boolean;
};

// The phase-2 BE emits a stable per-row id under this column name. The data
// page uses the value at this column's index as the DataGrid `gridRowId` and
// passes it as `row_id` to all /flowgate/selections/* endpoints.
export const FLOWGATE_ROW_ID_COLUMN = '__row_id';

// Filter DSL ops (AND-combined only, server-side).
export type FlowgateFilterOp =
  | 'eq'
  | 'ne'
  | 'lt'
  | 'le'
  | 'gt'
  | 'ge'
  | 'in'
  | 'contains'
  | 'starts_with'
  | 'is_null'
  | 'not_null';

export type FlowgateScalar = string | number | boolean;

export type FlowgateFilter =
  | {
      column: string;
      op: 'eq' | 'ne' | 'lt' | 'le' | 'gt' | 'ge' | 'contains' | 'starts_with';
      value: FlowgateScalar;
    }
  | { column: string; op: 'in'; value: FlowgateScalar[] }
  | { column: string; op: 'is_null' | 'not_null' };

export type FlowgateSort = {
  column: string;
  direction: 'asc' | 'desc';
};

// POST /flowgate/results/<id>/query/
export type FlowgateQueryRequest = {
  columns?: string[];
  filters?: FlowgateFilter[];
  sort?: FlowgateSort[];
  limit?: number; // 1..10000, default 100
  offset?: number; // default 0
};

// Rows arrive as positional arrays aligned to `columns`. The page zips them
// into `{ __rowId, [col]: value }` for the DataGrid.
export type FlowgateQueryResponse = {
  columns: string[];
  rows: Array<Array<FlowgateScalar | null>>;
  total: number;
  has_more: boolean;
};

// GET /flowgate/results/<id>/distinct/?column=...
export type FlowgateDistinctResponse = {
  column: string;
  values: Array<FlowgateScalar | null>;
  limit: number;
};

// POST /flowgate/results/<id>/query-domain/ — legacy-parity domain filters.
// The BE resolves hpo/gene/panel filters server-side to a gene-symbol predicate
// on the result's gene column; vcf_/annotation_ become column predicates
// (*_min/*_max → >=/<=, else contains). Returns the same shape as /query/.
export type FlowgateDomainFilters = {
  vcf_filters?: Record<string, string | number>;
  annotation_filters?: Record<string, string | number>;
  hpo_filters?: string[];
  gene_filters?: { include?: string[]; exclude?: string[] };
  gene_panel_ids?: string[];
  // When set, keep only rows whose Exomiser rank is this value or better
  // (rank 1 is best). Omitted → no rank limit; other filters apply to all rows.
  exomiser_rank?: number;
};

export type FlowgateQueryDomainRequest = {
  domain_filters: FlowgateDomainFilters;
  // Extra generic DSL predicates AND-combined with the resolved domain filters.
  filters?: FlowgateFilter[];
  columns?: string[];
  sort?: FlowgateSort[];
  limit?: number;
  offset?: number;
};

// GET /flowgate/results/<id>/download/?format=parquet — JSON body. csv/tsv
// stream the file directly and aren't typed.
export type FlowgateParquetDownloadResponse = {
  url: string;
  filename: string;
};

// Generic error body shape for the data endpoints.
// `code` is set for typed conditions (e.g. "no_row_id" when the result
// predates the phase-2 `__row_id` column).
export type FlowgateResultErrorBody = {
  detail?: string;
  code?: 'no_row_id' | string;
};

// ──────────────────────────────────────────────────────────────────────────────
// Selections, validation, comments (phase 2)
// ──────────────────────────────────────────────────────────────────────────────

export type FlowgateValidationStatus =
  | 'pending'
  | 'first_validated'
  | 'second_validated'
  | 'rejected';

export type FlowgateVariantReporting =
  | 'primary_finding'
  | 'secondary_finding'
  | 'incidental_finding';

export type FlowgateCommentType =
  | 'first_validation'
  | 'second_validation'
  | 'rejection'
  | 'general';

export type FlowgateSelectionComment = {
  id: string;
  comment_type: FlowgateCommentType;
  comment: string;
  created_by_email: string;
  created_at: string;
};

export type FlowgateSelection = {
  id: string;
  flowgate_result: string;
  row_id: number;
  row_snapshot: Record<string, unknown> | null;
  created_by_email: string;
  validation_status: FlowgateValidationStatus;
  variant_reporting: FlowgateVariantReporting | null;
  first_validated: boolean;
  first_validated_by_email: string | null;
  first_validation_date: string | null;
  second_validated: boolean;
  second_validated_by_email: string | null;
  second_validation_date: string | null;
  created_at: string;
  updated_at: string;
  comments: FlowgateSelectionComment[];
};

// POST /flowgate/selections/
export type CreateFlowgateSelectionRequest = {
  flowgate_result_id: string;
  row_id: number;
  row_snapshot?: Record<string, unknown>;
  variant_reporting?: FlowgateVariantReporting;
};

// POST /flowgate/selections/bulk-create/
export type BulkCreateFlowgateSelectionsRequest = {
  flowgate_result_id: string;
  row_ids: number[];
  labels?: Record<string, string>;
};
export type AlreadyInWorkbench = {
  row_id: number;
  created_by_email: string;
};

export type BulkCreateFlowgateSelectionsResponse = {
  created: number;
  already_existing: number;
  already_added: AlreadyInWorkbench[];
  selections: FlowgateSelection[];
};

// POST /flowgate/selections/bulk-{first,second}-validate/
export type BulkValidateRequest = {
  selection_ids: string[];
  comment?: string;
};
export type BulkValidateResponse = {
  updated: number;
  skipped: number;
};

// POST /flowgate/selections/bulk-delete/ — accepts either form
export type BulkDeleteFlowgateSelectionsRequest =
  | { selection_ids: string[] }
  | { flowgate_result_id: string; row_ids: number[] };
export type BulkDeleteFlowgateSelectionsResponse = { deleted: number };

// POST /flowgate/selections/<id>/reject/
export type RejectFlowgateSelectionRequest = {
  round_number: 1 | 2;
  comment: string;
  variant_label?: string;
};

// GET /flowgate/selections/validation-stats/?result=<uuid>
export type FlowgateValidationStats = {
  total: number;
  pending: number;
  first_validated: number;
  second_validated: number;
  rejected: number;
};

export type FlowgateDiscussionKind =
  | 'message'
  | 'variant_added'
  | 'variant_comment'
  | 'variant_rejected';

export type FlowgateDiscussionEntry = {
  id: string;
  kind: FlowgateDiscussionKind;
  body: string;
  row_id: number | null;
  variant_label: string;
  selection_id: string | null;
  author_email: string;
  author_name: string;
  created_at: string;
};

// GET /flowgate/selections/with-data/?result=<uuid>
export type FlowgateSelectionsWithData = {
  selections: FlowgateSelection[];
  data: FlowgateQueryResponse | null;
  data_error?: string;
};

// POST /flowgate/selections/<id>/comments/
export type CreateFlowgateSelectionCommentRequest = {
  comment_type: FlowgateCommentType;
  comment: string;
  variant_label?: string;
};
export type UpdateFlowgateSelectionCommentRequest = {
  comment: string;
};
